import {
  AUTO_FILL_MAX_LEVEL,
  getLevelAmount,
} from "../../config/autoFillProgramConfig";
import { describeChildAfpWithdrawalRules } from "../../config/autoFillChildWithdrawalConfig";
import {
  describePrimaryTrancheWithdrawalRules,
  filterWithdrawalsForProgram,
} from "../../config/autoFillWithdrawalSequencePolicy";
import { sumPointsCreditedFromLedgerRows, cappedCoPointsEarnedAtLevel } from "./autoFillLevelForfeiturePure";
import {
  isRootAutoFillProgram,
  usesSharedMemberPointsWalletForProgram,
} from "./autoFillProgramSpawn";
import { getWithdrawalCoPointsBreakdown } from "../../utils/autoFillCoPointsWallet";
import { getLevelForfeitureState } from "./autoFillLevelForfeitureTracking";
import {
  AUTO_FILL_LEVEL_FORFEITURE_MONTHS,
  levelForfeitureDeadlineDate,
  levelUnwithdrawnAmount,
  resolveForfeitureLevelMin,
} from "../../config/autoFillLevelForfeitureConfig";

function buildLevelForfeitureWindow(program, level) {
  if (!program) return null;
  const L = Number(level);
  if (L < resolveForfeitureLevelMin(program)) return null;
  const st = getLevelForfeitureState(program, L);
  if (!st?.reachedAt || st.forfeitureProcessedAt) return null;
  const deadline = levelForfeitureDeadlineDate(st.reachedAt);
  return {
    level: L,
    reachedAt: st.reachedAt,
    deadlineIso: deadline?.toISOString?.() || null,
    months: AUTO_FILL_LEVEL_FORFEITURE_MONTHS,
    unwithdrawn: levelUnwithdrawnAmount(st),
  };
}

/** Sum POINTS ledger credits to points wallet, grouped by matrix level. */
export function aggregatePointsEarnedByLevel(ledgerRows, userId) {
  const byLevel = {};
  ledgerRows.forEach((row) => {
    if (row.userId !== userId) return;
    if (row.transactionType !== "POINTS") return;
    if (row.toWallet && row.toWallet !== "POINTS") return;
    const L = Number(row.level) || 1;
    byLevel[L] = (byLevel[L] || 0) + (Number(row.amount) || 0);
  });
  return Object.keys(byLevel)
    .map((k) => Number(k))
    .sort((a, b) => a - b)
    .map((level) => ({
      level,
      amount: byLevel[level],
      unitAtLevel: getLevelAmount(level),
    }));
}

export function describeWithdrawalRulesForProgramLevel(
  programLevel,
  { isPrimaryAfp = true } = {}
) {
  const L = Number(programLevel);
  const lines = [];

  if (!isPrimaryAfp) {
    return describeChildAfpWithdrawalRules(L);
  }

  return describePrimaryTrancheWithdrawalRules(L);
}

export function actionForIneligibilityReason(reason) {
  if (reason.includes("pending withdrawal")) {
    return "Wait until admin approves or rejects your pending request, then try again.";
  }
  if (reason.includes("No active Auto Fill")) {
    return "Join or activate an Auto Fill program before requesting a withdrawal.";
  }
  if (reason.includes("T&C not accepted")) {
    return "On your Auto Fill program page, accept the program terms and conditions.";
  }
  if (reason.includes("V-Boost")) {
    return "In Profile Settings, complete your V-Boost wallet details and wait for admin verification.";
  }
  if (reason.includes("T&S program wallet")) {
    return "In Profile Settings, fill in your T&S program wallet and wait for admin approval.";
  }
  if (reason.includes("Direct referrals") || reason.includes("Direct Ultra")) {
    return "Share your referral link so more people meet the direct referral rules for this level.";
  }
  if (reason.includes("Team score")) {
    return "Add more qualified members at the required depth under your level-1 legs.";
  }
  if (reason.includes("Ultra Plus")) {
    return "Open Profile Settings, submit Ultra Plus details, and wait for admin approval.";
  }
  if (reason.includes("withdrawal") && reason.includes("must be completed")) {
    return "Finish the earlier tranche withdrawal in order; each request needs admin approval before the next.";
  }
  if (reason.includes("withdrawals 4 and 5") || reason.includes("withdrawal 4") || reason.includes("withdrawal 5")) {
    return "Complete and get admin approval for level 4 and level 5 CO tranche withdrawals first.";
  }
  return "Complete this requirement, then try again.";
}

/** CO Points earned / remaining per matrix level (1st payment of each cycle). */
export function computeLevelCoPointsBuckets(
  program,
  ledgerRows,
  userId,
  pointsWallet,
  { memberPointsWallet = null, filledMatrixCountByLevel = null } = {}
) {
  const currentLevel = Number(program?.currentLevel) || 1;
  const programId = program?.programId;
  if (!programId || !userId) return [];

  const useMatrixGate = filledMatrixCountByLevel != null;
  const earnedByLevel = {};
  for (let L = 1; L <= currentLevel; L += 1) {
    const filledAtL = useMatrixGate
      ? Number(filledMatrixCountByLevel[L] ?? filledMatrixCountByLevel[String(L)] ?? 0)
      : null;
    if (useMatrixGate && filledAtL === 0) {
      earnedByLevel[L] = 0;
      continue;
    }
    earnedByLevel[L] = useMatrixGate
      ? cappedCoPointsEarnedAtLevel(
          programId,
          userId,
          L,
          ledgerRows,
          filledAtL
        )
      : Math.min(
          getLevelAmount(L),
          sumPointsCreditedFromLedgerRows(programId, userId, L, ledgerRows)
        );
  }

  // Matrix credits land in autoFillPoints even when POINTS ledger rows are missing (e.g. replay / IW L2 auto).
  if (memberPointsWallet) {
    let ledgerSum = 0;
    for (let L = 1; L <= currentLevel; L += 1) {
      ledgerSum += earnedByLevel[L] || 0;
    }
    const walletEarned = Number(memberPointsWallet.earned) || 0;
    let extra = Math.max(0, walletEarned - ledgerSum);
    for (let L = 1; L <= currentLevel && extra > 0.001; L += 1) {
      const unit = getLevelAmount(L);
      const cur = earnedByLevel[L] || 0;
      const headroom = Math.max(0, unit - cur);
      const add = Math.min(extra, headroom);
      earnedByLevel[L] = cur + add;
      extra -= add;
    }
  }

  const totalAttributedOut =
    (Number(pointsWallet?.withdrawn) || 0) +
    (Number(pointsWallet?.locked) || 0);

  let fifoRemaining = totalAttributedOut;
  const withdrawnByLevel = {};
  for (let L = 1; L <= currentLevel; L += 1) {
    const earned = earnedByLevel[L] || 0;
    const take = Math.min(fifoRemaining, earned);
    withdrawnByLevel[L] = take;
    fifoRemaining -= take;
  }

  const buckets = [];
  for (let L = 1; L <= currentLevel; L += 1) {
    const unitAmount = getLevelAmount(L);
    const earned = earnedByLevel[L] || 0;
    const withdrawn = withdrawnByLevel[L] || 0;
    const available = Math.max(0, earned - withdrawn);
    buckets.push({
      level: L,
      unitAmount,
      earned,
      withdrawn,
      available,
    });
  }
  return buckets;
}

export function buildWithdrawalSlotBlockers(
  eligibility,
  bucket,
  walletAvailable
) {
  const blockers = [];
  const unit = bucket?.unitAmount || 0;
  const available = bucket?.available || 0;
  const L = bucket?.level;

  if (available < unit - 0.001) {
    blockers.push({
      reason:
        available <= 0
          ? `Level ${L} CO Points tranche (₹${unit.toLocaleString("en-IN")}) already withdrawn or not earned.`
          : `Level ${L} tranche: only ₹${available.toLocaleString("en-IN")} remaining (full tranche is ₹${unit.toLocaleString("en-IN")}).`,
      action: "Earn the matrix payment #1 at this level or wait until a prior tranche is settled.",
    });
  }

  if ((Number(walletAvailable) || 0) < unit - 0.001 && available >= unit - 0.001) {
    blockers.push({
      reason: "Your total CO Points wallet balance is lower than this tranche.",
      action: "Contact support if ledger and wallet totals do not match.",
    });
  }

  if (eligibility && !eligibility.eligible) {
    (eligibility.reasons || []).forEach((reason) => {
      blockers.push({
        reason,
        action: actionForIneligibilityReason(reason),
      });
    });
  }

  return blockers;
}

/** Genesis parent shares one autoFillPoints wallet with child AFPs — FIFO tranches use this program's withdrawals only. */
export function sumProgramPointsWithdrawalOutflow(pointsWithdrawals, program) {
  const rows = filterWithdrawalsForProgram(
    (pointsWithdrawals || []).filter((w) => w.walletType === "POINTS"),
    program
  );
  let withdrawn = 0;
  let locked = 0;
  rows.forEach((w) => {
    const amt = Number(w.amount) || 0;
    if (w.status === "COMPLETED") withdrawn += amt;
    else if (w.status === "PENDING") locked += amt;
  });
  return { withdrawn, locked };
}

export async function buildPerLevelWithdrawalDialogModel({
  program,
  pointsWallet,
  ledgerRows,
  userId,
  userProfile,
  userDocId,
  filledMatrixCountByLevel = null,
  pointsWithdrawals = [],
}) {
  const { evaluateAutoFillWithdrawalEligibility } = await import(
    "./autoFillWithdrawalEligibility"
  );
  const breakdown = getWithdrawalCoPointsBreakdown(
    userProfile,
    program,
    pointsWallet
  );
  const isGenesisRoot =
    program &&
    isRootAutoFillProgram(program) &&
    Number(program.enrollmentSeq) === 1;
  const fifoOutflow = isGenesisRoot
    ? sumProgramPointsWithdrawalOutflow(pointsWithdrawals, program)
    : {
        withdrawn: breakdown.totalWithdrawn,
        locked: breakdown.totalLocked,
      };
  const buckets = computeLevelCoPointsBuckets(
    program,
    ledgerRows,
    userId,
    {
      withdrawn: fifoOutflow.withdrawn,
      locked: fifoOutflow.locked,
    },
    {
      memberPointsWallet: usesSharedMemberPointsWalletForProgram(program)
        ? userProfile?.autoFillPoints
        : null,
      filledMatrixCountByLevel,
    }
  );
  const isPrimaryAfp = program ? isRootAutoFillProgram(program) : true;
  const walletAvailable = breakdown.totalAvailable;

  const levelSlots = [];
  for (const bucket of buckets) {
    if (bucket.earned <= 0 && bucket.available <= 0) continue;

    const eligibility = await evaluateAutoFillWithdrawalEligibility(
      userProfile,
      userDocId,
      program?.programId,
      { withdrawalProgramLevel: bucket.level }
    );
    const blockers = buildWithdrawalSlotBlockers(
      eligibility,
      bucket,
      walletAvailable
    );
    const canWithdraw = blockers.length === 0;

    levelSlots.push({
      ...bucket,
      rules: describeWithdrawalRulesForProgramLevel(bucket.level, {
        isPrimaryAfp,
      }),
      forfeitureWindow: buildLevelForfeitureWindow(program, bucket.level),
      eligibility,
      blockers,
      canWithdraw,
    });
  }

  return {
    programLevel: program?.currentLevel || 1,
    available: walletAvailable,
    earned: breakdown.totalEarned,
    withdrawn: breakdown.totalWithdrawn,
    locked: breakdown.totalLocked,
    walletBreakdown: breakdown,
    isPrimaryAfp,
    levelSlots,
  };
}

export function buildWithdrawalDialogModel({
  programLevel,
  pointsWallet,
  ledgerRows,
  userId,
  eligibility,
  isPrimaryAfp = true,
}) {
  const available = Number(pointsWallet?.available) || 0;
  const earned = Number(pointsWallet?.earned) || 0;
  const withdrawn = Number(pointsWallet?.withdrawn) || 0;
  const level = Number(programLevel) || 1;
  const atMaxLevel = level >= AUTO_FILL_MAX_LEVEL;
  const nextLevel = atMaxLevel ? level : level + 1;

  const earningsByLevel = aggregatePointsEarnedByLevel(ledgerRows, userId);
  const currentLevelUnit = getLevelAmount(level);
  const nextLevelUnit = getLevelAmount(nextLevel);

  const maxSuggested = available > 0
    ? Math.min(available, currentLevelUnit > 0 ? currentLevelUnit : available)
    : 0;

  return {
    programLevel: level,
    available,
    earned,
    withdrawn,
    earningsByLevel,
    currentLevelUnit,
    nextLevelUnit,
    maxSuggested,
    canWithdrawNow: eligibility?.eligible === true && available > 0,
    withdrawStatus: eligibility?.status || "—",
    isPrimaryAfp,
    currentRules: describeWithdrawalRulesForProgramLevel(level, { isPrimaryAfp }),
    nextRules: atMaxLevel
      ? ["You are at the highest program level."]
      : describeWithdrawalRulesForProgramLevel(nextLevel, { isPrimaryAfp }),
    atMaxLevel,
    eligibility,
    gates: eligibility?.gates,
    directReferrals: eligibility?.directReferrals,
  };
}
